import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TextField, FormControl, InputLabel, Button, Box, Paper, Checkbox, FormControlLabel, FormHelperText }
    from '@mui/material';
import FormSkeleton from '@components/FormSkeleton';
import CustomerSelect from '@components/customer/CustomerSelect';
import LangSelect from '@components/LangSelect';
import PasswordField from '@components/PasswordField';
import { isRoleAssociatedToCustomer } from '@utils/helpers';
import { createPasswordValidator } from '@utils/passwordValidation';
import RoleSelect from '@components/RoleSelect';

const UserForm = forwardRef(({ initialData, onSubmit, passwordRequired, children }, ref) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [role, setRole] = useState('');
    const [useRandomPassword, setUseRandomPassword] = useState(true);

    const validationSchema = Yup.object({
        first_name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        last_name: Yup.string()
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        email: Yup.string()
            .email(t('Invalid email format'))
            .max(255, t('Must be {{max}} characters or less', { max: 255 }))
            .required(t('This field is required')),
        lang: Yup.string()
            .required(t('This field is required')),
        password: Yup.string().when(['$useRandomPassword', '$passwordRequired'], {
            is: (useRandomPwd, pwdRequired) => useRandomPwd || !pwdRequired,
            then: () => Yup.string().nullable(),
            otherwise: () => createPasswordValidator(t, true),
        }),
        password_confirmation: Yup.string().when(['$useRandomPassword', '$passwordRequired'], {
            is: (useRandomPwd, pwdRequired) => useRandomPwd || !pwdRequired,
            then: () => Yup.string().nullable(),
            otherwise: () => Yup.string().oneOf([Yup.ref('password'), null], t('Passwords do not match')),
        }),
        role: Yup.string()
            .required(t('This field is required')),
        customer_id: Yup.string().when('role', {
            is: (value) => isRoleAssociatedToCustomer(value),
            then: () => Yup.string().required(t('This field is required')),
            otherwise: () => Yup.string().nullable(),
        }),
    });

    const { control, handleSubmit, setValue, setError, trigger, formState: { errors, touchedFields }, watch } = useForm({
        resolver: yupResolver(validationSchema),
        context: { passwordRequired, useRandomPassword },
        mode: 'onTouched',
        reValidateMode: 'onBlur',
        defaultValues: {
            active: true,
            password: '',
            password_confirmation: '',
        }
    });
    useImperativeHandle(ref, () => ({ setError })); // Permettre au composant parent d'appeler setError

    // Chargement des données initiales
    useEffect(() => {
        if (initialData) {
            initialData.customer_id = initialData.customer_id ? initialData.customer_id.toString() : '';
            initialData.password = '';
            initialData.password_confirmation = '';
            for (const [key, value] of Object.entries(initialData)) {
                setValue(key, value);
                if (key === 'role') {
                    setRole(value.toString());
                }
            }
        }
        setLoaded(true);
    }, [initialData, setValue, setRole]);

    // Pas de choix du client pour le rôle "SuperAdmin" ou "Internal API"
    useEffect(() => {
        if (role && !isRoleAssociatedToCustomer(role)) {
            setValue('customer_id', '');
        }
    }, [role, setValue]);

    const handleRandomPasswordChange = (event) => {
        setUseRandomPassword(event.target.checked);
        if (event.target.checked) {
            setValue('password', '');
            setValue('password_confirmation', '');
        }
    };

    // Mettre à jour le contexte de validation lorsque useRandomPassword change
    useEffect(() => {
        // Déclencher une revalidation des champs de mot de passe
        if (passwordRequired) {
            setValue('password', useRandomPassword ? '' : watch('password'), { shouldValidate: true });
            setValue('password_confirmation', useRandomPassword ? '' : watch('password_confirmation'), { shouldValidate: true });
        }
    }, [useRandomPassword, passwordRequired, setValue, watch]);

    const handleFormSubmit = (data) => {
        const formData = { ...data };
        
        if (useRandomPassword && passwordRequired) {
            // Si l'option de mot de passe aléatoire est activée, on ajoute le flag et on supprime les champs de mot de passe
            formData.send_random_password = true;
            delete formData.password;
            delete formData.password_confirmation;
        }
        
        onSubmit(formData);
    };

    if (!loaded) return <FormSkeleton nb={9} />;

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Paper sx={{ p: 2 }}>

                <Box mb={3}>
                    <Controller
                        name="first_name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('First name')}
                                variant="standard"
                                error={Boolean(errors.first_name)}
                                helperText={errors.first_name?.message}
                            />
                        )}
                    />
                </Box>
                <Box mb={3}>
                    <Controller
                        name="last_name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Last name')}
                                variant="standard"
                                error={Boolean(errors.last_name)}
                                helperText={errors.last_name?.message}
                            />
                        )}
                    />
                </Box>
                <Box mb={3}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                label={t('Email')}
                                variant="standard"
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                            />
                        )}
                    />
                </Box>

                <Box mb={3}>
                    <Controller
                        name="lang"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                {...field}
                                fullWidth
                                variant="standard"
                                required
                                error={Boolean(errors.lang)}
                            >
                                <InputLabel>{t('Language')}</InputLabel>
                                <LangSelect
                                    emptyLabel={t('- No selection -')}
                                    translatedInCurrentLanguage
                                    {...field}
                                />
                                {errors.lang ? <FormHelperText>{errors.lang.message}</FormHelperText> : ''}
                            </FormControl>
                        )}
                    />
                </Box>

                <Box mb={1}>
                    <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        color="primary"
                                        checked={field.value}
                                    />
                                }
                                label={t('Active|user')}
                            />
                        )}
                    />
                </Box>



                <Box mb={3}>
                    <Controller
                        name="role"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                {...field}
                                fullWidth
                                variant="standard"
                                required
                                error={Boolean(errors.role)}
                            >
                                <InputLabel>{t('Role')}</InputLabel>
                                <RoleSelect
                                    emptyLabel={t('- No selection -')}
                                    {...field}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setRole(e.target.value);
                                    }}
                                />
                                {errors.role ? <FormHelperText>{errors.role.message}</FormHelperText> : ''}
                            </FormControl>
                        )}
                    />
                </Box>

                <Box style={{ display: isRoleAssociatedToCustomer(role) ? 'block' : 'none' }} mb={3}>
                    <Controller
                        name="customer_id"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                fullWidth
                                variant="standard"
                                required={isRoleAssociatedToCustomer(role)}
                                error={Boolean(errors.customer_id)}
                            >
                                <InputLabel>{t('Customer')}</InputLabel>
                                <CustomerSelect
                                    emptyLabel={t('- No selection -')}
                                    {...field}
                                />
                                {errors.customer_id ? <FormHelperText>{errors.customer_id.message}</FormHelperText> : ''}
                            </FormControl>
                        )}
                    />
                </Box>

                {passwordRequired && (
                    <Box mb={1}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={useRandomPassword}
                                    onChange={handleRandomPasswordChange}
                                />
                            }
                            label={t('Generate a random password and send it by email')}
                        />
                    </Box>
                )}

                <Box mb={1}>
                    <Controller
                        name="password_force_change"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...field}
                                        color="primary"
                                        checked={field.value}
                                    />
                                }
                                label={t('Force user to change its password')}
                            />
                        )}
                    />
                </Box>

                {(!useRandomPassword || !passwordRequired) && (
                    <>
                        <Box mb={3}>
                            <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                    <PasswordField
                                        field={field}
                                        error={touchedFields.password && Boolean(errors.password)}
                                        helperText={touchedFields.password ? errors.password?.message : ''}
                                        label={t('Password')}
                                        required={passwordRequired}
                                        onBlur={(e) => {
                                            field.onBlur(e);
                                            trigger('password_confirmation');
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        <Box mb={3}>
                            <Controller
                                name="password_confirmation"
                                control={control}
                                render={({ field }) => (
                                    <PasswordField
                                        field={field}
                                        error={touchedFields.password_confirmation && Boolean(errors.password_confirmation)}
                                        helperText={touchedFields.password_confirmation ? errors.password_confirmation?.message : ''}
                                        label={t('Confirm password')}
                                        required={passwordRequired}
                                        showRules={false}
                                    />
                                )}
                            />
                        </Box>
                    </>
                )}

                {children}

            </Paper>
            <Box sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" color="primary">
                    {t('Save')}
                </Button>
            </Box>
        </form>
    );
});

export default UserForm;
